import { useEffect, useRef, useState } from "react";

/**
 * useReveal — attaches an IntersectionObserver to the returned ref and
 * flips `revealed` to true the first time the element enters the viewport.
 * Consumers add a class conditionally to trigger the CSS reveal animation.
 *
 * Safety fallback: if the observer hasn't fired within `fallbackMs`, we
 * mark the element as revealed anyway so content is never permanently hidden.
 */
export function useReveal({ threshold = 0.15, once = true, fallbackMs = 1500 } = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If browser doesn't support IntersectionObserver, reveal immediately.
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }

    let disposed = false;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (disposed) return;
        if (entry.isIntersecting) {
          setRevealed(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setRevealed(false);
        }
      },
      { threshold }
    );
    obs.observe(el);

    // Fallback — never let content stay invisible if the observer misfires.
    const timer = setTimeout(() => {
      if (!disposed) setRevealed(true);
    }, fallbackMs);

    return () => {
      disposed = true;
      clearTimeout(timer);
      obs.disconnect();
    };
  }, [threshold, once, fallbackMs]);

  return [ref, revealed];
}
